export type NetworkLinkObjectType = "campaign" | "audience" | "ad_group" | "ad";

export type NetworkLinkStatus =
  | "NOT_CREATED"
  | "CREATED"
  | "ACTIVE"
  | "PAUSED"
  | "POPULATED"
  | "PENDING_ARCHIVE"
  | "ARCHIVED";

export const NETWORK_LINK_STATUSES: NetworkLinkStatus[] = [
  "NOT_CREATED",
  "CREATED",
  "ACTIVE",
  "PAUSED",
  "POPULATED",
  "PENDING_ARCHIVE",
  "ARCHIVED",
];

export interface NetworkLinkState {
  object_type: NetworkLinkObjectType;
  object_id: number;
  object_name: string | null;
  network_ids: string[];
  current_network_id: string | null;
  status: NetworkLinkStatus;
  dirty: boolean;
  data: Record<string, any>;
  /** Audience links only (MPN-1971): members we uploaded to this network audience, beside
   *  the size the network reports for it and when it reported it. Null on other kinds,
   *  and matched_count stays null until the network has given a figure. */
  uploaded_members?: number | null;
  matched_count?: number | null;
  matched_at?: string | null;
}

/** "1 of 4,985 (0%)" -- what the network reports against what we uploaded. Null when
 *  there is nothing to compare yet. */
export function audienceSizeSummary(link: NetworkLinkState): string | null {
  if (link.uploaded_members == null || link.matched_count == null) return null;
  const pct = link.uploaded_members > 0
    ? ` (${Math.round((link.matched_count / link.uploaded_members) * 100)}%)`
    : "";
  return `${link.matched_count.toLocaleString()} of ${link.uploaded_members.toLocaleString()}${pct}`;
}

export interface LaunchNetworkLinks {
  launch_id: number;
  network: string;
  campaign: NetworkLinkState | null;
  audiences: NetworkLinkState[];
  ad_groups: NetworkLinkState[];
  ads: NetworkLinkState[];
}

// The links API returns object_type as backend domain model names, but the patch
// endpoint's `kind` path param and the rest of the FE expect the lowercase link
// kind. Normalize at the service boundary so object_type is consistent everywhere.
const OBJECT_TYPE_TO_KIND: Record<string, NetworkLinkObjectType> = {
  Launch: "campaign",
  AudienceDefinition: "audience",
  AdGroup: "ad_group",
  Ad: "ad",
};

export function normalizeLinkState(link: NetworkLinkState): NetworkLinkState {
  return { ...link, object_type: OBJECT_TYPE_TO_KIND[link.object_type] ?? link.object_type };
}
